import { useTheme } from '@mui/material';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Drawer, Loader, UnsavedChangesModal } from '../../components';
import { RECEIVER_EMAIL } from '../../constants/shipping';
import useModal from '../../hooks/useModal';
import useWindowSize from '../../hooks/useWindowSize';
import { editEngagementRequest, isEditEngagementCancellation } from '../../store/actions/engagement';
import { selectIsEditEngagementFetching } from '../../store/selectors/reports';
import { TEditEngagementPayload } from '../../types/bucket';
import {
  EditSendModeEnum,
  EditSendSubmitResultEnum,
  IReportEngagementItem,
  IReportWithCampaignSummaries,
} from '../../types/reports';
import { IReportsRouteParams } from '../../types/routing';
import { DrawerAnimationDirectionEnum, IApiError, NotificationListEnum } from '../../types/shell';
import { IReceiverInfoDetailsFormValues } from '../../types/shipping';
import { isObjectsEqual } from '../../utils/helpers';
import notification from '../../utils/notification';
import { getEditableSendFields, normalizeReceiverEmail } from '../../utils/reports';
import EditEngagementDetailsContainer, {
  IEditDetailsRef,
} from './EditEngagementDetailsContainer/EditEngagementDetailsContainer';
import EditEngagementItemsContainer, {
  IEditItemsRef,
} from './EditEngagementItemsContainer/EditEngagementItemsContainer';

import styles from './EditSendSidebar.module.scss';

const DEFAULT_EDIT_ERROR_MESSAGE = 'Something bad has happened. The send edits were not applied.';

const EDIT_ERROR_FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Please check the entered details and try again.',
  409: 'This email is already used by another send of this One Link.',
};

interface IEditSendErrorResponse {
  status: number;
  body?: unknown;
}

const isEditSendErrorResponse = (error: unknown): error is IEditSendErrorResponse =>
  typeof error === 'object' &&
  error !== null &&
  !(error instanceof Error) &&
  typeof (error as { status?: unknown }).status === 'number';

const getClientErrorMessage = (body: unknown): string | undefined => {
  if (typeof body !== 'object' || body === null) {
    return undefined;
  }
  const { message } = body as { message?: unknown };
  return typeof message === 'string' && message.trim() ? message.trim() : undefined;
};

const getEditSendErrorMessage = (error: unknown): string => {
  if (!isEditSendErrorResponse(error)) {
    return DEFAULT_EDIT_ERROR_MESSAGE;
  }
  const { status, body } = error;
  const clientMessage = status >= 400 && status < 500 ? getClientErrorMessage(body) : undefined;
  return clientMessage || EDIT_ERROR_FALLBACK_MESSAGES[status] || DEFAULT_EDIT_ERROR_MESSAGE;
};

interface IProps {
  onClose: () => void;
  mode?: EditSendModeEnum;
  report?: IReportWithCampaignSummaries | null;
  onSave?: () => void;
}

const EditSendSidebar = ({ mode, report, onSave, onClose }: IProps) => {
  const { sass: variables } = useTheme();
  const dispatch = useDispatch();

  const isLoading = useSelector(selectIsEditEngagementFetching);

  const detailsRef = React.useRef<IEditDetailsRef>(null);
  const inFlightSubmitRef = React.useRef<Promise<EditSendSubmitResultEnum> | null>(null);
  const itemsRef = React.useRef<IEditItemsRef>(null);

  const { reportId = '', type } = useParams<IReportsRouteParams>();

  const { openModal: openSaveChangesModal, closeModal: closeSaveChangesModal, Modal: SaveChangesModal } = useModal();

  const reportDetails = React.useMemo(() => getEditableSendFields(report), [report]);
  const reportItems = React.useMemo(() => report?.items, [report]);

  const handleSubmit = React.useCallback(
    (candidate: TEditEngagementPayload): Promise<EditSendSubmitResultEnum> => {
      if (inFlightSubmitRef.current) {
        return inFlightSubmitRef.current;
      }

      const successfulSubmit = () => {
        onClose();
        if (onSave) {
          // timeout is a workaround to wait for some time for the data is updated in DB
          setTimeout(onSave, 500);
        }
      };

      const submission: Promise<EditSendSubmitResultEnum> = new Promise<IApiError | void>((resolve, reject) => {
        dispatch(
          editEngagementRequest({
            engagement: { ...candidate, type, engagement_id: reportId },
            resolve,
            reject,
          }),
        );
      })
        .then((response: any) => {
          const warning = response?.body?.warning;

          if (warning?.message) {
            notification.warning(NotificationListEnum.EditEngagement, {
              content: warning.message,
            });
          } else {
            notification.success(NotificationListEnum.EditEngagement, {
              content: 'The send was updated successfully!',
            });
          }
        })
        .then(() => {
          successfulSubmit();
          return EditSendSubmitResultEnum.Saved;
        })
        .catch((error: unknown) => {
          if (isEditEngagementCancellation(error)) {
            return EditSendSubmitResultEnum.Cancelled;
          }
          notification.error(NotificationListEnum.EditEngagement, {
            content: getEditSendErrorMessage(error),
          });
          return EditSendSubmitResultEnum.Error;
        })
        .finally(() => {
          if (inFlightSubmitRef.current === submission) {
            inFlightSubmitRef.current = null;
          }
        });

      inFlightSubmitRef.current = submission;
      return submission;
    },
    [dispatch, type, reportId, onSave, onClose],
  );

  const handleSubmitDetails = React.useCallback(
    async (details: IReceiverInfoDetailsFormValues) => {
      const { note_message, receiver_email, ...otherDetails } = details;
      const email = normalizeReceiverEmail(receiver_email);
      const receiverInfo =
        email === normalizeReceiverEmail(report?.[RECEIVER_EMAIL])
          ? otherDetails
          : { ...otherDetails, receiver_email: email };
      const filteredDetails = note_message?.trim() ? { ...receiverInfo, note_message } : receiverInfo;
      return handleSubmit({ receiver_info: filteredDetails });
    },
    [handleSubmit, reportItems, report],
  );

  const handleSubmitItems = React.useCallback(
    async (items: IReportEngagementItem[]) => {
      return handleSubmit({ items_info: items });
    },
    [handleSubmit, reportDetails],
  );

  const handleSaveAndCloseModal = React.useCallback(async () => {
    const items = itemsRef.current?.getValues();
    switch (mode) {
      case EditSendModeEnum.Items:
        if (items) {
          await handleSubmitItems(items);
        }
        break;
      case EditSendModeEnum.Details:
        await detailsRef.current?.submit();
        break;
      default:
        onClose();
    }
  }, [handleSubmitItems, mode, onClose]);

  const saveChangesModalActions = React.useMemo(() => {
    return [
      { title: 'Save and close', onClick: handleSaveAndCloseModal },
      {
        title: 'Discard changes',
        onClick: () => {
          if (onClose) {
            onClose();
          }
        },
        isOutlined: true,
      },
    ];
  }, [onClose, handleSaveAndCloseModal]);

  const saveChangesModal = React.useMemo(
    () => (
      <SaveChangesModal
        overlayClassName={styles.saveChangesModalOverlay}
        className={cn(styles.saveChangesModal, 'common-modal')}
      >
        {() => <UnsavedChangesModal actions={saveChangesModalActions} onClose={closeSaveChangesModal} />}
      </SaveChangesModal>
    ),
    [closeSaveChangesModal, saveChangesModalActions, SaveChangesModal],
  );

  const handleCloseSidebar = React.useCallback(() => {
    if (
      mode === EditSendModeEnum.Details &&
      detailsRef.current &&
      !isObjectsEqual(detailsRef.current?.getValues(), reportDetails)
    ) {
      return openSaveChangesModal();
    }

    if (
      mode === EditSendModeEnum.Items &&
      itemsRef.current?.getValues() &&
      !isEqual(itemsRef.current?.getValues(), reportItems)
    ) {
      return openSaveChangesModal();
    }

    if (typeof onClose === 'function') {
      return onClose();
    }
  }, [openSaveChangesModal, onClose, reportDetails, reportItems, mode]);

  const { mobile, HD } = useWindowSize();

  const from = React.useMemo(() => (mobile ? variables.boxSidebarMinHeight : variables.boxSidebarMinWidth), [mobile]);
  const to = React.useMemo(() => {
    if (mobile) {
      return variables.boxSidebarMaxHeight;
    }

    if (HD) {
      return variables.boxSidebarMaxWidthHD;
    }

    return variables.boxSidebarMaxWidthFullHD;
  }, [mobile, HD]);

  return (
    <React.Fragment>
      <Drawer
        className={cn(styles.container)}
        direction={mobile ? DrawerAnimationDirectionEnum.Vertically : DrawerAnimationDirectionEnum.Horizontally}
        from={from}
        to={to}
        trigger={!!mode}
        onClose={handleCloseSidebar}
        withOverlay
        overlayClassName={styles.overlay}
      >
        <Loader isLoading={isLoading && !!mode} />
        {mode && (
          <div className={styles.contentContainer}>
            <div className={styles.closeButtonContainer}>
              <button className={styles.closeButton} onClick={handleCloseSidebar} />
            </div>
            <div className={styles.formContainer}>
              {mode === EditSendModeEnum.Details && (
                <EditEngagementDetailsContainer
                  ref={detailsRef}
                  report={report}
                  onSubmit={handleSubmitDetails}
                  onCancel={onClose}
                />
              )}
              {mode === EditSendModeEnum.Items && (
                <EditEngagementItemsContainer ref={itemsRef} report={report} onSubmit={handleSubmitItems} />
              )}
            </div>
          </div>
        )}
      </Drawer>
      {saveChangesModal}
    </React.Fragment>
  );
};

export default EditSendSidebar;
